import { Field } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { FixedRand, ThreeWayArrow } from 'src/assets/images';
import { PaymentType } from 'src/services/payment-request-service';
import { GeneratePaymentRequestModal } from 'src/containers/self-service/generate-payment-request/components/GeneratePaymentRequestModal';
import { Loader } from 'src/containers/self-service/generate-payment-request/components/Loader';
import { RequestCategoryItem } from 'src/containers/self-service/generate-payment-request/components/RequestCategoryItem';
import { TechnicalErrorModal } from 'src/containers/self-service/generate-payment-request/components/TechnicalErrorModal';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { isPaymentRequestEnabled } from 'src/services/payment-request-service';
import { paymentsDashboardRoute } from 'src/routing/routes';
import { GeneratePaymentRequestWizardPageProps } from 'src/containers/self-service/generate-payment-request/components/GeneratePaymentRequestWizard';
import {
  LinkName,
  PageName,
  PaymentRequestTealiumService,
  UIInteractionType,
} from 'src/containers/self-service/generate-payment-request/tagging/payment-request-tealium-service';

export enum CategoryType {
  FixedAmount = 'Fixed Payment',
  VariableAmount = 'Variable Payment',
}

export function PreferredPaymentRequest({
  validateForm,
  formValues,
  setOnNextEventHandler,
}: GeneratePaymentRequestWizardPageProps) {
  const history = useHistory();
  const [isLoading, setIsLoading] = React.useState(true);
  const [showTechError, setShowTechError] = React.useState(false);
  const [error, setError] = React.useState<Error>();
  const [showEnablePaymentRequestModal, setShowEnablePaymentRequestModal] = React.useState(false);
  const { merchant } = useMerchantProfile();

  const paymentRequestEnabled = React.useCallback(async (id: string) => {
    try {
      setIsLoading(true);
      const isEnabled = await isPaymentRequestEnabled(id ?? '');
      setIsLoading(false);
      setShowEnablePaymentRequestModal(!isEnabled);
    } catch (error) {
      setIsLoading(false);
      setError(error as Error);
      setShowTechError(true);
      PaymentRequestTealiumService.onTechnicalError('500', PageName.GENERATE_PAYMENT_REQUEST);
    }
  }, []);

  React.useEffect(() => {
    if (!!merchant.merchantId) {
      paymentRequestEnabled(merchant.merchantId);
    }
    validateForm();
  }, [merchant.merchantId]);

  React.useEffect(() => {
    PaymentRequestTealiumService.onPaymentRequestStart();
    setOnNextEventHandler(() => {
      PaymentRequestTealiumService.onUIInteraction(
        LinkName.GENERATE_PAYMENT_REQUEST,
        UIInteractionType.Next,
        UIInteractionType[formValues.categoryType],
      );
    });
  }, []);

  return (
    <div>
      <GeneratePaymentRequestModal
        visible={showEnablePaymentRequestModal}
        onClose={() => setShowEnablePaymentRequestModal(false)}
      />

      <TechnicalErrorModal
        error={error}
        visible={showTechError}
        onRetry={() => {
          setShowTechError(false);
          setError(undefined);
          merchant.merchantId && paymentRequestEnabled(merchant.merchantId);
        }}
        onClose={() => history.push(paymentsDashboardRoute)}
      />

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <section className="p-[25px] md:text-center">
            <h1 className="text-base font-medium text-grey-100 md:text-4xl">
              Please select your preferred payment request category
            </h1>

            <h3 className="mt-2 text-xs md:text-base text-white-50">
              Choose a payment option: Fixed payment for a fixed amount and Variable payment to let
              the customer choose.
            </h3>
          </section>
          <section className="flex items-center justify-center gap-4 mt-4 md:mt-14 md:gap-8">
            <Field
              value={PaymentType.FixedAmount}
              type={'radio'}
              name={'categoryType'}
              component={RequestCategoryItem}
              icon={FixedRand}
              label={CategoryType.FixedAmount}
              tooltip="Selecting this option ensures that payment can only be made for the amount specified."
            />
            <Field
              value={PaymentType.VariableAmount}
              type={'radio'}
              name={'categoryType'}
              component={RequestCategoryItem}
              icon={ThreeWayArrow}
              resetFields={['transactionAmount', 'transactionReference']}
              label={CategoryType.VariableAmount}
              tooltip="Variable payment allows the customer to choose the amount to pay."
            />
          </section>
        </>
      )}
    </div>
  );
}
